/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  emptyMarketWarningsSnapshot,
  fetchMarketWarningCanonicalMaps,
  fetchVendorMarketWarnings,
  loadMarketWarningCanonicalMapsCache,
  loadMarketWarningsCache,
  marketWarningKey,
  normalizeWarningVendor,
  resolveMarketWarningBase,
  saveMarketWarningCanonicalMapsCache,
  saveMarketWarningsCache,
  type MarketIdentity,
  type MarketWarningsSnapshot,
  type MarketWarningVendor,
} from '../services/marketWarnings';

export const MARKET_WARNINGS_REFRESH_MS = 5 * 60 * 1_000;
export const MARKET_WARNINGS_REQUEST_TIMEOUT_MS = 15_000;

export interface MarketWarningsContextValue {
  snapshot: MarketWarningsSnapshot;
  errors: Record<MarketWarningVendor, string | null>;
  isMarketWarned: (market: MarketIdentity) => boolean;
  getWarnedVendors: (markets: readonly MarketIdentity[]) => MarketWarningVendor[];
  refresh: () => Promise<void>;
}

const defaultValue: MarketWarningsContextValue = {
  snapshot: emptyMarketWarningsSnapshot(),
  errors: { upbit: null, bithumb: null },
  isMarketWarned: () => false,
  getWarnedVendors: () => [],
  refresh: async () => undefined,
};

export const MarketWarningsContext =
  createContext<MarketWarningsContextValue>(defaultValue);

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const MarketWarningsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [snapshot, setSnapshot] = useState(loadMarketWarningsCache);
  const [canonicalMaps, setCanonicalMaps] = useState(
    loadMarketWarningCanonicalMapsCache,
  );
  const [errors, setErrors] = useState<
    Record<MarketWarningVendor, string | null>
  >({ upbit: null, bithumb: null });
  const mountedRef = useRef(false);
  const inFlightRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const abortControllersRef = useRef<AbortController[]>([]);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const generation = ++requestGenerationRef.current;
    const vendors: MarketWarningVendor[] = ['upbit', 'bithumb'];
    const controllers = [...vendors, 'canonical-maps'].map(
      () => new AbortController(),
    );
    abortControllersRef.current = controllers;
    try {
      const runWithTimeout = async <T,>(
        controller: AbortController,
        task: () => Promise<T>,
      ): Promise<T> => {
        const timeout = window.setTimeout(
          () => controller.abort(),
          MARKET_WARNINGS_REQUEST_TIMEOUT_MS,
        );
        try {
          return await task();
        } finally {
          window.clearTimeout(timeout);
        }
      };
      const [mapsResult, ...results] = await Promise.allSettled([
        runWithTimeout(controllers[0], () =>
          fetchMarketWarningCanonicalMaps(controllers[0].signal),
        ),
        ...vendors.map(async (vendor, index) => {
          const controller = controllers[index + 1];
          return runWithTimeout(controller, () =>
            fetchVendorMarketWarnings(vendor, controller.signal),
          );
        }),
      ]);
      if (
        !mountedRef.current ||
        generation !== requestGenerationRef.current
      ) {
        return;
      }

      const now = Date.now();
      if (mapsResult.status === 'fulfilled') {
        setCanonicalMaps(mapsResult.value);
        saveMarketWarningCanonicalMapsCache(mapsResult.value);
      }
      setSnapshot((previous) => {
        const next: MarketWarningsSnapshot = {
          upbit: previous.upbit,
          bithumb: previous.bithumb,
        };
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            next[vendors[index]] = { warnings: result.value, updatedAt: now };
          }
        });
        saveMarketWarningsCache(next);
        return next;
      });
      setErrors((previous) => {
        const next = { ...previous };
        results.forEach((result, index) => {
          next[vendors[index]] =
            result.status === 'fulfilled' ? null : errorMessage(result.reason);
        });
        return next;
      });
    } finally {
      if (generation === requestGenerationRef.current) {
        inFlightRef.current = false;
        abortControllersRef.current = [];
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const interval = window.setInterval(() => {
      void refresh();
    }, MARKET_WARNINGS_REFRESH_MS);

    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current = [];
      inFlightRef.current = false;
      window.clearInterval(interval);
    };
  }, [refresh]);

  const warningSets = useMemo(
    () => ({
      upbit: new Set(snapshot.upbit.warnings),
      bithumb: new Set(snapshot.bithumb.warnings),
    }),
    [snapshot],
  );

  const isMarketWarned = useCallback(
    (market: MarketIdentity) => {
      const vendor = normalizeWarningVendor(market.exchange);
      const vendorBase = vendor
        ? resolveMarketWarningBase(vendor, market.base, canonicalMaps)
        : market.base;
      const key = marketWarningKey(market.exchange, market.quote, vendorBase);
      return Boolean(vendor && key && warningSets[vendor].has(key));
    },
    [canonicalMaps, warningSets],
  );

  const getWarnedVendors = useCallback(
    (markets: readonly MarketIdentity[]) => {
      const warned = new Set<MarketWarningVendor>();
      for (const market of markets) {
        const vendor = normalizeWarningVendor(market.exchange);
        if (vendor && isMarketWarned(market)) warned.add(vendor);
      }
      return (['upbit', 'bithumb'] as const).filter((vendor) =>
        warned.has(vendor),
      );
    },
    [isMarketWarned],
  );

  const value = useMemo<MarketWarningsContextValue>(
    () => ({ snapshot, errors, isMarketWarned, getWarnedVendors, refresh }),
    [snapshot, errors, isMarketWarned, getWarnedVendors, refresh],
  );

  return (
    <MarketWarningsContext.Provider value={value}>
      {children}
    </MarketWarningsContext.Provider>
  );
};

export function useMarketWarnings(): MarketWarningsContextValue {
  return useContext(MarketWarningsContext);
}
